import { Injectable } from '@angular/core';

import { SupportService } from '../support/support.service';
import { CallStatsService } from '../../services/call-stats/call-stats.service';
import { MicStream } from '../../models/mic-stream/mic-stream';

import { AnalyticEventService } from '../analytic-event/analytic-event.service';
import { LoggingService } from '../logging/logging.service';

import { IssueDetectionService } from 'src/app/services/issue-detection/issue-detection.service';
import { ParticipantsListService } from '../participants-list/participants-list.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { IssueRemediationService } from '../issue-remediation/issue-remediation.service';
import { VirtualBackgroundService } from '../virtual-background/virtual-background.service';
import { NotificationService } from '../notification/notification.service';

// Helpers
import { MediaDeviceManager } from './media-device-manager';
import { VolumeMonitor } from './volume-monitor';
import { ScreenShareManager } from './screen-share-manager';
import { StreamTrackManager } from './stream-track-manager';

declare global {
  interface HTMLVideoElement {
    setSinkId: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class StreamService {
  public fullStream: MediaStream;
  public myStream: MediaStream;
  public micStream: MicStream;
  public mainStream: MediaStream;
  public shareVideoTrack: MediaStreamTrack;
  public shareAudioTrack: MediaStreamTrack;
  public shareLoading: boolean;
  public videoInputId: string;
  public audioInputId: string;
  public audioOutputId: string;
  public audioSwitch: boolean;
  public videoSwitch: boolean;
  public sharing: boolean;
  public mainStreamId: string;
  public streamLoaded: boolean;
  public pinned: string;
  public hasVideoDevice: boolean;
  public hasAudioDevice: boolean;
  public videoMissing = false;
  public talkingOnMute = false;

  private userId: string;
  private roomId: string;
  private publishCb: CallableFunction;
  private unpublishAllCb: CallableFunction;
  private unpublishSharingCb: CallableFunction;

  private mediaDeviceManager: MediaDeviceManager;
  private volumeMonitor: VolumeMonitor;
  private screenShareManager: ScreenShareManager;
  private trackManager: StreamTrackManager;

  constructor(
    private support: SupportService,
    private callstats: CallStatsService,
    private analytics: AnalyticEventService,
    private logging: LoggingService,
    private issueDetection: IssueDetectionService,
    private issueRemediation: IssueRemediationService,
    private participantsList: ParticipantsListService,
    private localStorage: LocalStorageService,
    private background: VirtualBackgroundService,
    private notification: NotificationService,
  ) {
    this.mediaDeviceManager = new MediaDeviceManager(
      this.support,
      this.callstats,
      this.localStorage,
      this.participantsList,
      () => ({
        audioInputId: this.audioInputId,
        videoInputId: this.videoInputId,
        audioOutputId: this.audioOutputId,
        roomId: this.roomId,
        userId: this.userId,
      }),
      (patch) => {
        if (patch.audioInputId !== undefined) { this.audioInputId = patch.audioInputId; }
        if (patch.videoInputId !== undefined) { this.videoInputId = patch.videoInputId; }
        if (patch.audioOutputId !== undefined) { this.audioOutputId = patch.audioOutputId; }
      },
    );

    this.volumeMonitor = new VolumeMonitor(
      this.support,
      this.logging,
      this.notification,
      this.participantsList,
      () => ({
        micStream: this.micStream,
        fullStream: this.fullStream,
        audioSwitch: this.audioSwitch,
        userId: this.userId,
      }),
      (value: boolean) => { this.talkingOnMute = value; },
      () => this.setMainStream(),
    );

    this.screenShareManager = new ScreenShareManager(
      this.analytics,
      this.callstats,
      this.issueDetection,
      this.issueRemediation,
      this.logging,
      this.participantsList,
      () => ({
        shareVideoTrack: this.shareVideoTrack,
        shareAudioTrack: this.shareAudioTrack,
        sharing: this.sharing,
        shareLoading: this.shareLoading,
        mainStream: this.mainStream,
        mainStreamId: this.mainStreamId,
        pinned: this.pinned,
        userId: this.userId,
      }),
      (patch) => {
        if (patch.shareVideoTrack !== undefined) { this.shareVideoTrack = patch.shareVideoTrack; }
        if (patch.shareAudioTrack !== undefined) { this.shareAudioTrack = patch.shareAudioTrack; }
        if (patch.sharing !== undefined) { this.sharing = patch.sharing; }
        if (patch.mainStreamId !== undefined) { this.mainStreamId = patch.mainStreamId; }
      },
      () => this.publishCb,
      () => this.unpublishSharingCb,
    );

    this.trackManager = new StreamTrackManager(
      this.callstats,
      this.analytics,
      this.logging,
      this.issueDetection,
      this.issueRemediation,
      this.participantsList,
      this.background,
      () => ({
        fullStream: this.fullStream,
        myStream: this.myStream,
        micStream: this.micStream,
        audioSwitch: this.audioSwitch,
        videoSwitch: this.videoSwitch,
        streamLoaded: this.streamLoaded,
        videoMissing: this.videoMissing,
        shareVideoTrack: this.shareVideoTrack,
        shareAudioTrack: this.shareAudioTrack,
        roomId: this.roomId,
        userId: this.userId,
      }),
      (patch) => {
        if (patch.audioSwitch !== undefined) { this.audioSwitch = patch.audioSwitch; }
        if (patch.videoSwitch !== undefined) { this.videoSwitch = patch.videoSwitch; }
        if (patch.streamLoaded !== undefined) { this.streamLoaded = patch.streamLoaded; }
        if (patch.videoMissing !== undefined) { this.videoMissing = patch.videoMissing; }
      },
      () => this.mediaDeviceManager.getVideoOptions(),
      () => this.mediaDeviceManager.getAudioOptions(),
      () => this.getVideoTrack(),
      () => this.getAudioTrack(),
      () => this.setupMyVolume(),
      () => this.setMainStream(),
      () => this.volumeMonitor.connectVolumeSource(),
      () => this.volumeMonitor.disconnectVolumeSource(),
      () => this.unpublishAllCb,
    );
  }

  public startStream(
    userId: string,
    roomId: string,
    publishCb: CallableFunction,
    unpublishAllCb: CallableFunction,
    unpublishSharingCb: CallableFunction
  ): void {
    this.userId = userId;
    this.roomId = roomId;
    this.publishCb = publishCb;
    this.unpublishAllCb = unpublishAllCb;
    this.unpublishSharingCb = unpublishSharingCb;

    this.fullStream = new MediaStream();
    this.myStream = new MediaStream();
    this.micStream = new MicStream();
    this.mainStream = new MediaStream();

    this.videoSwitch = true;
    this.audioSwitch = true;
    this.streamLoaded = null;

    this.shareLoading = false;
    this.sharing = false;
    this.pinned = null;
  }

  public closeStream(): void {
    this.stopVideo();
    if (this.fullStream) {
      delete this.fullStream;
      this.fullStream = null;
    }
    if (this.micStream) {
      delete this.micStream;
      this.micStream = null;
    }
    if (this.mainStream) {
      delete this.mainStream;
      this.mainStream = null;
    }
    this.volumeMonitor.clearMediaStreamSource();
  }

  public loadMyVolume(): void {
    this.volumeMonitor.loadMyVolume();
  }

  public setupMyVolume(): void {
    this.volumeMonitor.setupMyVolume();
  }

  public unloadMyVolume(): void {
    this.volumeMonitor.unloadMyVolume();
  }

  public refreshVolume(id: string, volume: number, timestamp: number, remoteTimeMakingNoise?: number): void {
    this.volumeMonitor.refreshVolume(id, volume, timestamp, remoteTimeMakingNoise);
  }

  public getAudioTrack(): MediaStreamTrack {
    const audio = this.fullStream.getAudioTracks();
    if (audio.length > 0) {
      return audio[0];
    }
    return null;
  }

  public getVideoTrack(): MediaStreamTrack {
    if (this.background.isActive) {
      return this.background.getVideoTrack();
    }
    const video = this.fullStream.getVideoTracks();
    if (video.length > 0) {
      return video[0];
    }
    return null;
  }

  public async setupDefaultIO(): Promise<void> {
    return this.mediaDeviceManager.setupDefaultIO();
  }

  public updateIO(data: any, outputCb?: boolean): void {
    this.mediaDeviceManager.updateIO(data, outputCb);
  }

  public setOutput(id: string, outputId?: string): void {
    this.mediaDeviceManager.setOutput(id, outputId);
  }

  public async reverseCamera(): Promise<void> {
    return this.mediaDeviceManager.reverseCamera();
  }

  public async startSharing(): Promise<void> {
    return this.screenShareManager.startSharing();
  }

  public stopSharing(): void {
    this.screenShareManager.stopSharing();
  }

  public setSharing(track: MediaStreamTrack): void {
    this.screenShareManager.setSharing(track);
  }

  public unsetSharing(): void {
    this.screenShareManager.unsetSharing();
  }

  public setMainStream(force?: boolean): void {
    this.screenShareManager.setMainStream(force);
  }

  public stop(kind: string): void {
    this.trackManager.stop(kind);
  }

  public start(kind: string): Promise<MediaStream> {
    return this.trackManager.start(kind);
  }

  public stopVideo(): void {
    this.trackManager.stopVideo();
  }

  public async loadVideo(): Promise<void> {
    return this.trackManager.loadVideo();
  }

  public replaceTrack(kind: string): void {
    this.trackManager.replaceTrack(kind);
  }
}
